// Admin product forms (/admin/product/form/:id?, /admin/product/:platform/:id).
// Spec: docs/specs/2026-10-07-bo-form-draft-clear-design.md §5 (B1–B4).
//
// The product being edited is loaded from GET /api/v1/products/:id by the id
// in the route — never handed over through $route.params.params — and lives
// only in the form component's own data. These helpers are the pure parts of
// that load, shared by Admin/ProductForm.vue and Admin/PlatformProductForm.vue.
// Pinned by tests/unit/adminProductFormState.spec.js.
import { taskbarPathOf } from "./taskbar";

// The product id the route asks for, as a string; "" in create mode.
export function routeProductId(route) {
	const params = route && route.params && typeof route.params === "object" ? route.params : {};
	const id = params.id;
	if (id === undefined || id === null) return "";
	const s = String(id).trim();
	return s;
}

// Does the kept-alive instance rendered with `vnodeKey` show `route`?
// DashboardLayout keys every taskbar page by its tab key (the path), so the
// instance for /admin/product/form/5 keeps watching $route after the user opens
// /admin/product/form/7 in another tab. That instance must not load product 7
// into its own data — the "ข้อมูลปนกัน" bug of §1. No key (not keyed by the
// layout) = the instance follows the route.
export function viewOwnsRoute(vnodeKey, route) {
	if (vnodeKey === undefined || vnodeKey === null) return true;
	if (!route || typeof route !== "object") return false;
	const full = route.fullPath || route.path || "";
	return vnodeKey === taskbarPathOf(full) || vnodeKey === full;
}

// Zero/sentinel dates (0001-…, 0000-…) show as empty in a picker.
export function cleanDate(value) {
	if (!value) return null;
	const s = String(value);
	if (s.startsWith("0001") || s.startsWith("0000")) return null;
	return value;
}

// §5 B4: the App (shopping page) fields are no longer on screen, but the V3
// update writes all five (`Select(... app_* ...).Updates`), so the form keeps
// carrying them: create = these defaults, edit = the values loaded from the API.
export function appFieldDefaults() {
	return {
		app_enable: false,
		app_tag: "",
		app_discount: 0,
		app_discount_start: null,
		app_discount_end: null
	};
}

export function appFieldsFrom(product) {
	const p = product || {};
	return {
		app_enable: p.app_enable == 1,
		app_tag: p.app_tag || "",
		app_discount: p.app_discount || 0,
		app_discount_start: cleanDate(p.app_discount_start),
		app_discount_end: cleanDate(p.app_discount_end)
	};
}

// bo_product_sku.wms_map is a JSON string (or "" / NULL / "{}") — the forms
// work on arrays. Never throws: a broken cell becomes [].
export function parseSkuWmsMaps(skus) {
	if (!Array.isArray(skus)) return [];
	return skus.map(sku => {
		const out = Object.assign({}, sku);
		let map = out.wms_map;
		if (typeof map === "string") {
			try {
				map = map === "" ? [] : JSON.parse(map);
			} catch (e) {
				map = [];
			}
		}
		out.wms_map = Array.isArray(map) ? map : [];
		return out;
	});
}

// attributes is a JSON string column; null / "" / "{}" / broken = [].
export function parseAttributes(raw) {
	let parsed = raw;
	if (typeof raw === "string") {
		try {
			parsed = raw === "" ? [] : JSON.parse(raw);
		} catch (e) {
			parsed = [];
		}
	}
	return Array.isArray(parsed) ? parsed : [];
}

// price_tiers is a JSON string column; empty/null = no tiers.
export function parsePriceTiers(raw) {
	if (!raw) return [];
	try {
		const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
		if (!Array.isArray(parsed)) return [];
		return parsed.map(t => ({
			min_qty: Number(t.min_qty) || 0,
			price: Number(t.price) || 0
		}));
	} catch (e) {
		return [];
	}
}
